import React from "react";
import { Button, Modal } from "./ui";

const STATI = {
  100000000: "Pianificato",
  100000001: "In scadenza",
  100000002: "Scaduto",
};

const EFFETTI = {
  elimina: { label: "Verrà eliminata", className: "text-red-700" },
  scollega: {
    label: "Resta, senza la tipologia scollegata",
    className: "text-yellow-700",
  },
  bloccata: {
    label: "Resta: ha un intervento collegato",
    className: "text-gray-600",
  },
};

/**
 * Tipologie che il salvataggio sta per scollegare dal fornitore e le sue scadenze
 * aperte che le coprono; risolve con { scadenze, puoEliminare } oppure null se non
 * c'è nulla da chiedere.
 */
export async function caricaScadenzeCollegate(
  apiRequest,
  fornitoreId,
  tipologieAttuali,
  tipologieNuove,
) {
  const rimosse = tipologieAttuali.filter((id) => !tipologieNuove.includes(id));
  if (!fornitoreId || rimosse.length === 0) return null;

  const result = await apiRequest(
    `/fornitori/${fornitoreId}/tipologie-intervento/scadenze-collegate?tipologieIds=${rimosse.join(",")}`,
  );
  if (!result.value?.length) return null;
  return { scadenze: result.value, puoEliminare: !!result.puoEliminare };
}

/** Riassunto, per il toast, di cosa è successo alle scadenze dopo il salvataggio. */
export function riassuntoScadenze(result) {
  if (result?.scadenzeError) {
    return "le scadenze collegate non sono state eliminate";
  }
  const esito = result?.scadenze;
  if (!esito) return null;
  const parti = [`${esito.eliminate.length} scadenze eliminate`];
  if (esito.aggiornate.length > 0) {
    parti.push(`${esito.aggiornate.length} aggiornate`);
  }
  if (esito.nonEliminate.length > 0) {
    parti.push(`${esito.nonEliminate.length} non eliminate`);
  }
  return parti.join(", ");
}

/**
 * ScadenzeCollegateModal - chiede cosa fare delle scadenze aperte di un fornitore
 * quando gli si scollega una tipologia di intervento.
 *
 * Props:
 * - isOpen: boolean
 * - scadenze: righe di GET /fornitori/:id/tipologie-intervento/scadenze-collegate
 * - puoEliminare: boolean (solo Admin)
 * - saving: boolean
 * - onScelta: (scelta: 'elimina' | 'lascia') => void
 * - onClose: () => void
 */
function ScadenzeCollegateModal({
  isOpen,
  scadenze = [],
  puoEliminare = false,
  saving = false,
  onScelta,
  onClose,
}) {
  const daEliminare = scadenze.filter((s) => s.azione === "elimina").length;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Scadenze collegate alle tipologie rimosse"
      size="2xl"
      closeOnOverlayClick={false}
      footer={
        <div className="flex flex-wrap justify-end gap-3">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Annulla
          </Button>
          <Button
            variant="outline"
            onClick={() => onScelta("lascia")}
            disabled={saving}
          >
            Lascia le scadenze
          </Button>
          {puoEliminare && (
            <Button
              variant="danger"
              onClick={() => onScelta("elimina")}
              disabled={saving}
            >
              Elimina le scadenze
            </Button>
          )}
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Il fornitore ha {scadenze.length}{" "}
          {scadenze.length === 1 ? "scadenza aperta" : "scadenze aperte"} per le
          tipologie che stai scollegando. Puoi eliminarle insieme
          all&apos;associazione oppure lasciarle: in quel caso restano assegnate
          al fornitore e continuano a generare promemoria.
        </p>

        <div className="max-h-80 overflow-y-auto border rounded-lg divide-y">
          {scadenze.map((s) => (
            <div key={s.id} className="p-3 text-sm">
              <div className="flex items-start justify-between gap-3">
                <div className="font-medium text-gray-900">
                  {s.dataScadenza
                    ? new Date(s.dataScadenza).toLocaleDateString("it-IT")
                    : "-"}
                  {s.rsa && ` · ${s.rsa}`}
                </div>
                <span className="text-xs text-gray-500 whitespace-nowrap">
                  {STATI[s.stato] || "-"}
                </span>
              </div>
              <div className="text-gray-700 mt-1">
                {s.tipologieRimosse.map((t) => t.nome).join(", ")}
                {s.tipologieRestanti.length > 0 && (
                  <span className="text-gray-500">
                    {" "}
                    (insieme a{" "}
                    {s.tipologieRestanti.map((t) => t.nome).join(", ")})
                  </span>
                )}
              </div>
              {puoEliminare && EFFETTI[s.azione] && (
                <div className={`text-xs mt-1 ${EFFETTI[s.azione].className}`}>
                  Se elimini: {EFFETTI[s.azione].label}
                </div>
              )}
            </div>
          ))}
        </div>

        {puoEliminare ? (
          <p className="text-xs text-gray-500">
            L&apos;eliminazione riguarda {daEliminare}{" "}
            {daEliminare === 1 ? "scadenza" : "scadenze"} e non è reversibile.
            L&apos;eventuale evento già creato nel calendario Outlook non viene
            rimosso.
          </p>
        ) : (
          <p className="text-xs text-gray-500">
            Solo un amministratore può eliminare le scadenze.
          </p>
        )}
      </div>
    </Modal>
  );
}

export default ScadenzeCollegateModal;
